// Learning Center — step-by-step "How to do this" guidance, keyed by curriculum
// group id (see learningContent.js). The Learning Center renders GUIDE[group.id]
// as a collapsible "How to do this" panel under each section so learners aren't
// just ticking boxes — they're taught how to do each thing.
//
// Written for the people who USE AriaOne. Steps use the real on-screen control
// names. The prose version of all of this lives in
// src/content/AriaOne_Learning_Guide.md.

export const GUIDE = {
  // ───────── Level 1 ─────────
  "l1-a": {
    intro: "Everything starts from the left menu. Here's how to get around it.",
    steps: [
      "Confirm your practice: the top of the left menu shows the practice name and its number. Click it to see every practice on your account and pick a different one — every screen then reloads for that practice.",
      "Shrink or grow the menu: click the chevron at the menu's edge to collapse it to icons, and again to expand it.",
      "Find a page by its group: the menu is split into Scheduling, Clinical and Billing — scan those headings.",
      "Read the to-do numbers: the small number on a menu row is a count — Encounter review = notes waiting to be signed, Claims queue = claims to refresh, Chart/Clinic documents = files still being read.",
      "Find your tools: Settings, the text-size control and your account initials all sit at the bottom of the menu.",
    ],
  },
  "l1-b": {
    intro: "Home is your daily starting point — three stacked areas.",
    steps: [
      "'Needs you today' lists the most urgent fixes (usually visits missing a payer). Open one and act on it right there.",
      "'Today's uploads / in progress' shows bands — In progress, ERA, Failed, Missing, Unbalanced, Duplicate, Audit. Click a band to expand the items behind it.",
      "The activity area shows whether the system is working or idle, with a link into the activity dashboard (Lumina at work).",
    ],
  },
  "l1-c": {
    intro: "Patients is your roster and the way into any chart.",
    steps: [
      "Open Patients from the menu, then type a name or MRN in the search box to filter the list.",
      "Turn on 'Medical data only' to hide registration-only patients and show those with chart activity.",
      "Read a row: name, age, DOB, MRN, a 'Medical data / Registration only' badge, provider, insurance, program chips, recent codes, last visit and next appointment.",
      "Click a row to open the full chart. Rows with no chart aren't clickable.",
      "Use 'Load more' at the bottom to page through more patients.",
    ],
  },
  "l1-d": {
    intro: "Book and manage telehealth visits from the Telehealth page.",
    steps: [
      "Use the Schedule tab to book; the Activity tab shows visits being turned into notes after the call.",
      "Narrow the list with the status switch (Open / Waiting / Completed / Cancelled / All), the date range, the provider filter and the search box.",
      "Switch between List and Week with the view toggle.",
      "To book: click New visit → pick the patient (search by name/MRN) → choose a visit type / reason → confirm the phone or email → choose the provider → pick a time → Book visit.",
      "Share the join link with 'Copy patient link' from the success message or the visit row.",
      "Scheduled = a set time from open slots. On demand ('Book for now') = starts now even with no open slot; the provider presses Start when ready.",
      "Reschedule, Complete or Start a visit using the buttons on its row.",
    ],
  },
  "l1-e": {
    intro: "A first look at where notes and documents live.",
    steps: [
      "Open Encounter review to see finished visit notes; each row shows the suggested codes and whether the note is signed.",
      "Upload a document: open Chart documents → Upload → drag files in (or Choose files) → Upload N files. Watch it being read in the tracker.",
      "Accepted files: PDF, JPG, PNG or HEIC, up to 50 MB each.",
    ],
  },
  "l1-f": {
    intro: "Two habits that prevent most confusion.",
    steps: [
      "If something you expect isn't showing, check which practice you're in and widen the date range first.",
      "When you need help, note the practice, the page, the patient or visit, and exactly what you clicked — it makes the answer much faster.",
    ],
  },

  // ───────── Level 2 ─────────
  "l2-a": {
    intro: "The note editor is where you refine a note and its codes. It opens from several places but works the same everywhere.",
    steps: [
      "Open it from Encounter review (Edit note), Dictation, a claim's Encounter-note tab, or a visit.",
      "If you see a 'Custom note / Standard SOAP' toggle, it tells you which version the codes are read from.",
      "Add chart info: in the right 'Pull from chart' panel, tick items under Allergies, Medications, Problems, Vitals, Labs or Documents, then '+ Add N to note'. They appear in a red 'NOT SAVED' banner.",
      "Save keeps your text but marks the codes as needing a refresh; Save & recode rebuilds the note with the added items and refreshes the codes.",
      "Cancel asks a second time before discarding, and leaving warns you if there are unsaved changes.",
    ],
  },
  "l2-b": {
    intro: "Manage insurance is one shared panel, reached from the payer chips, the coverage bar, or the claim.",
    steps: [
      "Missing payer / 'needs id': type the payer name (2+ letters) in the search and pick the match; if the list is empty it falls back to your curated payer list.",
      "Set the primary or secondary payer: enter Member ID and Group, then Save primary / Save secondary.",
      "'On file → Use this' appears when the matched payer differs from the saved one — click it to use the matched payer.",
      "Scan card: upload a photo or PDF of the insurance card and AriaOne reads it to fill the payer.",
      "Copy from history: in the History panel, click → Primary or → Secondary on a prior coverage and confirm the preview.",
      "Save once and AriaOne remembers it, updating other waiting visits with the same payer (a message says how many).",
      "Many at once: click the 'N need payer id' pill, pick the correct payer for each group, then Apply to N visits.",
    ],
  },
  "l2-c": {
    intro: "Running telehealth from booking through the call.",
    steps: [
      "Week view booking: one provider → click an open time on the hour grid; several providers → hover a day cell and click '+ Book · N open'. Fill the QuickBook card → Book visit.",
      "If 'that time was just taken' appears, pick another slot.",
      "In the time picker, use the Morning / Afternoon / Evening groups and the 'next opening' jump.",
      "Row actions: Start, Copy link, Open chart, Message, Rebook, Reschedule, Complete, End session, Delete.",
      "During the call, the panel under the video has a summary strip and tabs (Just in, Docs, Intake, Questions, Messages, Care, Session, Info).",
      "On Activity, an amber dot or a stalled step means press Retry; 'the patient never joined' means there's nothing to retry.",
    ],
  },
  "l2-d": {
    intro: "Review, fix and sign a finished note in Encounter review.",
    steps: [
      "Open a row → read the note on the left and the codes on the right → Edit note if needed → Recode to refresh the codes → Sign encounter.",
      "To change a signed note: Unsign to edit (a warning shows if a claim was already made) → edit → Recode → Sign again.",
      "Codes panel: reorder diagnoses with ↑/↓, remove or copy them; set CPT units and diagnosis pointers; review prescriptions.",
      "Hover or pin a diagnosis chip to see the sentence in the note it came from.",
      "Change log: click it to see who changed what and when; Download CSV for a copy.",
    ],
  },
  "l2-e": {
    intro: "Dictation turns spoken audio into a coded note.",
    steps: [
      "Open Dictation → search a patient → Dictate; it opens their chart tab and records and transcribes into a note. (A patient with no chart can't be dictated into.)",
      "The sessions table shows payer, code counts, charge and claim status; use refresh and the Today / 7d / 30d / All switch.",
      "Expand a session to Recode from note after edits, or Open claim.",
    ],
  },
  "l2-f": {
    intro: "Uploading and finding documents.",
    steps: [
      "Upload one file, several, or a mixed bundle that auto-splits. Patient documents go to Chart documents; letters and notices go to Clinic documents.",
      "Watch the Split Uploads Tracker ('N processing') as files are read.",
      "Find documents with the search box (name or MRN), the Sort & Filter popover, and the pager (50 per page).",
      "A file that didn't read: open the To review / failed item and re-file it, or Retry.",
    ],
  },
  "l2-g": {
    intro: "The Claims queue is where claims are finished and sent.",
    steps: [
      "Build from documents: Claims queue → Upload claims → pick the provider → drop 1–10 files → AriaOne splits them by patient and date of service.",
      "Resolve a 'To review' row: match the roster patient (name/MRN) and pick a date of service — no re-upload needed.",
      "Open a claim: fix Diagnoses (Box 21) and Service lines (Box 24 — code, units, charge, pointers); clear everything under 'What has to be fixed before it saves'; the visit must be signed → Save claim.",
      "Refresh stale claims with 'Recode all' on the out-of-date band.",
    ],
  },
  "l2-h": {
    intro: "Payment posting turns remittances into 835 files.",
    steps: [
      "Open Payment posting → upload a remittance (single or a bundle); each file uploads once and duplicates are caught.",
      "Use the ledger's search and filters (check number, amount, payer).",
      "Open a file → the ERA workspace → reconcile the lines, use the auto-balance helper, and read the Quality/readiness tab.",
      "Set the primary payer and billing NPI → Generate 835 → Download 835 → Mark as downloaded. For many at once: Generate all / Download all (ZIP) from the ledger.",
    ],
  },

  // ───────── Level 3 ─────────
  "l3-a": {
    intro: "Settings → Practice Setup configures everything AriaOne uses. Work the steps in order.",
    steps: [
      "Providers · NPI: add a provider and link the NPI — they then appear to pick on claims (Rendering) and in Billing relationships.",
      "Availability: for each provider, toggle each day on, add one or more start–end windows ('+ Add window'), set a slot length and buffer, and use 'Copy Monday to weekdays'. These become the bookable slots in Telehealth.",
      "Intake form builder: add Questions, write the Direction guidance, and use Preview.",
      "Note template: pick a provider → Note style → upload one past note (it learns the layout only, never patient details) → turn on Exam defaults and attest them → Preview.",
      "Visit types: use Auto-Build Visit Types (needs at least one provider linked) → tick the ones to add → Add. Open any type to edit it across its tabs (Basics, Booking, Note, Billing, Programs, Advanced).",
      "Set 'Default for unmatched imports' so imported appointments that match no type still land somewhere.",
      "Coding rules: type an Always/Never rule and press Enter; add rules at the practice level and per provider — the rule count then shows on a claim.",
    ],
  },
  "l3-b": {
    intro: "The change history is a permanent, read-only record of every note change.",
    steps: [
      "Open a note's Change log. Entries are colour-coded: note edits (green), coding/claim (amber), sign/unsign (indigo).",
      "Each entry shows the action, who made it (and their IP), the time, +N/−N line counts, the specific codes added or removed, and a before/after diff.",
      "Download CSV to rebuild a timeline or share it.",
      "It can't be edited or deleted — to correct something, make a new change on top.",
    ],
  },
  "l3-c": {
    intro: "Reconciling a harder remittance in the ERA workspace.",
    steps: [
      "Reconcile each claim line against the remittance; use the auto-balance helper; edit or reset a line as needed.",
      "Add an adjustment: enter a reason (CARC) code plus a non-zero amount; remove any that don't belong.",
      "Clear everything on the Quality/readiness tab, then Generate 835; if it fails, read the list of issues it returns.",
      "Work the ledger at scale: Generate all, Download all (ZIP), Save, delete, and find failed or 'older held' items.",
    ],
  },
  "l3-d": {
    intro: "Lumina at work tells you what the system is doing and how long things take.",
    steps: [
      "Open Lumina at work (from the activity tile). Each card — documents, splits, coding, remittances, telehealth, dictations — shows running now, idle, done, and typical seconds.",
      "Use the time-window switcher; Today/live figures are exact, other windows are approximate.",
      "Match a stuck visit, a long upload or a slow refresh to its card to see where things stand.",
      "Open the Usage stats drawer (Today / 7 / 30 days) for most-used pages and load-time trends.",
    ],
  },
  "l3-e": {
    intro: "How to work through the common tricky cases.",
    steps: [
      "Two insurances: set primary and secondary in Manage insurance and confirm the '2nd · payer' badge shows.",
      "Payer read wrong from a document: use 'On file → Use this', copy from history (confirm the preview), or Scan card; then check other visits updated.",
      "Visit unsigned after a claim was made: re-sign, Recode, and Save again.",
      "Pages filed under the wrong patient or date: reopen the Multi-part uploads batch and use Resolve.",
      "Surprising codes on a custom note: switch to Standard SOAP to see what was coded, then Recode.",
      "'Estimated' totals: a rate is missing — add it in Settings (Codes or the payer).",
      "A visit nobody joined: choose Rebook, End session, or Delete as appropriate.",
    ],
  },

  // ───────── Level 4 ─────────
  "l4-a": {
    intro: "Becoming fluent in the whole product.",
    steps: [
      "Practise the one-line summary and be ready to jump to any part of it on screen.",
      "Walk the full journey yourself once, end to end (Settings → book → visit → note → sign → claim → payment → 835 → history), so you can narrate it to someone else.",
      "Open the same patient in the chart, the documents and the insurance panel to show the information matches.",
      "Keep a short cheat-sheet of what each number, badge and status means.",
    ],
  },
  "l4-b": {
    intro: "Helping your team get up to speed.",
    steps: [
      "Use Level 1 of this course as your first-day script for a new user.",
      "Sit with a teammate and let them drive the daily workflow while you prompt.",
      "For each feature, set a small 'do this in the app' task so people learn by doing.",
      "When the product changes, update your team's quick answers.",
      "Hold a short, regular 'tricky cases' review.",
    ],
  },
  "l4-c": {
    intro: "Leading a smooth setup and training sessions.",
    steps: [
      "For a new practice, follow Practice Setup end to end: providers, availability, a note template per provider, visit types and coding rules — then plan the first week.",
      "Run a billing session on insurance, the group 'needs payer id' fix and two-insurance cases.",
      "Run a clinical session on the note editor, pull-from-chart and signing.",
      "Run a scheduling session on availability, booking and running visits.",
    ],
  },
  "l4-d": {
    intro: "Good judgement when something's unfamiliar.",
    steps: [
      "Reason from how the product fits together (scheduling → notes → claims → payments) to a safe next step.",
      "Know the actions that can't be undone — deleting a visit, deleting remittance rows, the permanent history, discarding unsaved changes, unsigning after a claim — and warn people before they act.",
      "When something is genuinely beyond the app, know when to ask for help rather than guess.",
    ],
  },
};
